import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { EMPTY, take, switchMap } from 'rxjs';
import { CustomAttributesService } from '../../../services/custom-attributes.service';

interface CustomAttributeField {
  lookUpName: string;
  fieldName: string;
  fieldValue: string;
  fieldId: string;
  controlKey: string;
  dataType: string;
  editable: boolean;
  visible: boolean;
  entraValue: string;
  defaultValue: string;
  allowedValuesEnabled: boolean;
  allowedValues: string[];
  tooltip: string;
  errorMessage: string;
  isMatched: boolean;
  preferEntraPlaceholder?: boolean;
  emptyValue?: string;
  isSaving?: boolean;
  saveMessage?: string;
  saveMessageClass?: string;
}
@Component({
  selector: 'xware-me-attributes',
  templateUrl: './me-attributes.component.html',
  styleUrls: ['./me-attributes.component.scss'],
})
export class MeAttributesComponent implements OnInit, OnDestroy {
  callCounter = 0;
  userFirstName: string;
  userLastName: string;
  userForm: FormGroup;
  loadingIndicator: boolean;
  isSaving: boolean = false;
  saveMessage: string = '';
  saveMessageClass: string = '';
  customAttributeFormGroup = new FormControl({});
  adType = sessionStorage.getItem('adType');
  bGoogle = this.adType == 'gapi';
  fileName: string;
  noEditableFieldsMessage: string = '';
  isFeatureDisabled = false;

  formFields: CustomAttributeField[] = [];
  private originalFormValues = new Map<string, string>();
  readonly dropdownUnsetValue = '__NOT_SET__';
  readonly resetTooltip = this.bGoogle
    ? 'Reset to the original directory value'
    : 'Reset to the original Entra ID value';

  constructor(
    private customAttributesService: CustomAttributesService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.userForm = this.fb.group({});
    this.loadingIndicator = true;

    this.loadCustomAttributes();
  }

  ngOnDestroy(): void {}

  private loadCustomAttributes(): void {
    this.customAttributesService
      .checkLookupOverrideEnabled()
      .pipe(
        switchMap(enabled => {
          if (!enabled) {
            this.loadingIndicator = false;
            this.isFeatureDisabled = true;
            return EMPTY;
          }
          return this.customAttributesService.GetCustomAttributesValues().pipe(take(1));
        })
      )
      .subscribe(
        (result: any) => {
          if (result && result.success && result.data && Array.isArray(result.data)) {
            const customAttributesArray = result.data.map((item: any) => {
              const settings = item.settings || {};
              const allowedValuesSettings = settings.allowedValues || {};
              const allowedValuesEnabled = !!allowedValuesSettings.enabled;
              const fieldId = item.fieldId || item.customFieldId || item.fieldName;
              return {
                lookUpName: item.fieldName,
                fieldName: item.fieldName,
                fieldValue: item.value ?? '',
                fieldId,
                controlKey: '',
                dataType: settings.dataType || 'string',
                editable: !!settings.userEditable,
                visible: !!settings.userEditable,
                entraValue: item.entraValue ?? '',
                defaultValue: settings.defaultValue ?? '',
                allowedValuesEnabled,
                allowedValues: allowedValuesEnabled ? allowedValuesSettings.values || [] : [],
                tooltip: '',
                errorMessage: '',
                isMatched: true,
              };
            });

            this.populateFormFields(customAttributesArray);
          } else {
            this.loadingIndicator = false;
            this.noEditableFieldsMessage = this.resolveApiMessage(
              result?.message,
              this.loadErrorFallback
            );
          }
        },
        error => {
          this.loadingIndicator = false;
          this.noEditableFieldsMessage = this.resolveApiMessage(
            error?.error?.message ?? error?.error ?? error?.message ?? error,
            this.loadErrorFallback
          );
          console.error('Error:', this.noEditableFieldsMessage || 'Unknown error');
        }
      );
  }

  populateFormFields(data: CustomAttributeField[]): void {
    this.userFirstName = '';
    this.userLastName = '';
    const formGroupConfig = {};

    const visibleAttributes = data.filter(item => item.visible);
    this.originalFormValues.clear();

    const usedKeys = new Set<string>();
    this.formFields = visibleAttributes.map((field, index) => {
      const baseKey = field.fieldId || field.lookUpName || 'field_' + index;
      let controlKey = baseKey;
      let suffix = 1;
      while (usedKeys.has(controlKey)) {
        controlKey = baseKey + '_' + suffix++;
      }
      usedKeys.add(controlKey);
      return {
        ...field,
        controlKey,
        fieldValue: field.fieldValue ?? '',
        tooltip: field.tooltip || '',
        errorMessage: field.errorMessage || '',
        isMatched: true,
        preferEntraPlaceholder: false,
        emptyValue: '',
      };
    });

    this.formFields.forEach(field => {
      if (field.lookUpName === 'FirstName') {
        this.userFirstName = field.fieldValue || field.entraValue || '';
      } else if (field.lookUpName === 'LastName') {
        this.userLastName = field.fieldValue || field.entraValue || '';
      }
      this.applyTextPlaceholderState(field);
      const initialValue = this.getInitialFieldValue(field);
      formGroupConfig[field.controlKey + '_LookUpName'] = new FormControl(field.lookUpName);
      formGroupConfig[field.controlKey + '_FieldName'] = new FormControl(field.fieldName);
      formGroupConfig[field.controlKey + '_FieldValue'] = new FormControl(
        initialValue,
        field.allowedValuesEnabled ? [] : [Validators.maxLength(500), this.whitespaceOnlyValidator]
      );
    });

    this.userForm = this.fb.group(formGroupConfig);
    this.formFields.forEach(field => {
      this.originalFormValues.set(
        field.controlKey,
        this.getEffectiveFieldValue(field)
      );
    });
    this.loadingIndicator = false;
  }

  onRefresh(controlKey: string): void {
    const field = this.formFields.find(attr => attr.controlKey === controlKey);
    if (!field) {
      console.warn(`No match found for ${controlKey}`);
      return;
    }
    const formControl = this.userForm.get(controlKey + '_FieldValue');
    if (formControl) {
      if (field.allowedValuesEnabled) {
        const resetValue = this.getResetValue(field);
        if (!this.normalizeFormValue(field.entraValue)) {
          formControl.setValue(
            this.normalizeFormValue(resetValue) ? resetValue : this.dropdownUnsetValue
          );
        } else {
          formControl.setValue(resetValue);
        }
      } else {
        field.preferEntraPlaceholder = true;
        field.emptyValue = field.entraValue || '';
        formControl.setValue('');
      }
    } else {
      console.warn(`Form control not found for ${controlKey}`);
    }
  }

  onTextInput(controlKey: string): void {
    const field = this.formFields.find(f => f.controlKey === controlKey);
    if (!field || field.allowedValuesEnabled) {
      return;
    }
    const formControl = this.userForm.get(controlKey + '_FieldValue');
    if (!formControl) {
      return;
    }
    if (this.normalizeFormValue(formControl.value)) {
      field.preferEntraPlaceholder = false;
      field.emptyValue = '';
    }
  }

  onInputBlur(controlKey: string): void {
    const field = this.formFields.find(f => f.controlKey === controlKey);
    if (!field || field.allowedValuesEnabled) {
      return;
    }
    const formControl = this.userForm.get(controlKey + '_FieldValue');
    if (!formControl || typeof formControl.value !== 'string') {
      return;
    }
    const trimmed = formControl.value.trim();
    if (formControl.value !== trimmed) {
      formControl.setValue(trimmed, { emitEvent: true });
    }
  }

  private trimAllTextInputs(): void {
    this.formFields
      .filter(field => field.editable && !field.allowedValuesEnabled)
      .forEach(field => this.onInputBlur(field.controlKey));
  }

  onSaveAll(): void {
    this.saveMessage = '';
    this.trimAllTextInputs();

    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      this.saveMessageClass = 'error-message';
      return;
    }

    this.isSaving = true;

    const fieldsToSave = this.formFields
      .filter(field => field.editable)
      .filter(field => this.hasFieldChanged(field))
      .map(field => {
        const effectiveValue = this.getEffectiveFieldValue(field);
        field.fieldValue = effectiveValue;

        return {
          fieldId: field.fieldId,
          fieldName: field.fieldName,
          value: this.getValueToSend(effectiveValue, field),
        };
      });

    this.customAttributesService.saveCustomAttributes(fieldsToSave).subscribe(
      saveResponse => {
        this.isSaving = false;
        if (saveResponse && saveResponse.success) {
          this.updateSavedValues();
          this.saveMessage = 'All changes saved successfully';
          this.saveMessageClass = 'success-message';
        } else {
          this.saveMessage = this.resolveApiMessage(saveResponse?.message, this.saveErrorFallback);
          this.saveMessageClass = 'error-message';
        }
        setTimeout(() => {
          this.saveMessage = '';
        }, 3000);
      },
      saveError => {
        this.isSaving = false;
        this.saveMessage = this.resolveSaveErrorMessage(saveError);
        this.saveMessageClass = 'error-message';
        setTimeout(() => {
          this.saveMessage = '';
        }, 3000);
        console.error('Error saving custom attributes:', saveError);
      }
    );
  }

  onSave(controlKey: string): void {
    this.trimAllTextInputs();
    const formControlFieldValue = this.userForm.get(controlKey + '_FieldValue');
    const field = this.formFields.find(f => f.controlKey === controlKey);
    if (formControlFieldValue && formControlFieldValue.invalid) {
      formControlFieldValue.markAsTouched();
      if (field) {
        const message = formControlFieldValue.hasError('whitespaceOnly')
          ? this.whitespaceOnlyErrorMessage
          : 'Maximum 500 characters allowed.';
        this.updateFieldSaveMessage(controlKey, message, 'error-message');
      }
      return;
    }
    if (field) {
      field.isSaving = true;
      field.saveMessage = '';
    }

    const effectiveValue = field
      ? this.getEffectiveFieldValue(field)
      : this.normalizeFormValue(formControlFieldValue ? formControlFieldValue.value : '');
    if (field) {
      field.fieldValue = effectiveValue;
    }

    const valueToSend = field ? this.getValueToSend(effectiveValue, field) : '';

    const fieldsToSave = [
      {
        fieldId: field?.fieldId || controlKey,
        fieldName: field?.fieldName || controlKey,
        value: valueToSend,
      },
    ];

    this.customAttributesService.saveCustomAttributes(fieldsToSave).subscribe(
      saveResponse => {
        if (field) {
          field.isSaving = false;
          if (saveResponse && saveResponse.success) {
            this.originalFormValues.set(controlKey, effectiveValue);
            this.syncTextFieldDisplay(field);
            this.syncDropdownUnsetFormValue(field);
            this.updateFieldSaveMessage(controlKey, 'Saved', 'success-message');
          } else {
            this.updateFieldSaveMessage(
              controlKey,
              this.resolveApiMessage(saveResponse?.message, this.saveErrorFallback),
              'error-message'
            );
          }
        }
      },
      saveError => {
        const errorMessage = this.resolveSaveErrorMessage(saveError);
        console.error('Error saving custom attribute:', errorMessage);
        if (field) {
          field.isSaving = false;
          this.updateFieldSaveMessage(controlKey, errorMessage, 'error-message');
        }
      }
    );
  }

  getPlaceholder(field: CustomAttributeField): string {
    if (field.preferEntraPlaceholder) {
      return field.entraValue || '';
    }
    return field.defaultValue || field.entraValue || '';
  }

  isSelectionField(field: CustomAttributeField): boolean {
    return field.allowedValuesEnabled;
  }

  showPlaceholderDropdownOption(field: CustomAttributeField): boolean {
    return !this.normalizeFormValue(field.entraValue);
  }

  showEmptyDropdownOption(field: CustomAttributeField): boolean {
    return !!this.normalizeFormValue(field.entraValue);
  }

  isDropdownUnsetSelected(field: CustomAttributeField): boolean {
    if (
      this.normalizeFormValue(field.defaultValue) ||
      this.normalizeFormValue(field.entraValue)
    ) {
      return false;
    }
    const formControl = this.userForm.get(field.controlKey + '_FieldValue');
    if (!formControl) {
      return false;
    }
    return (
      formControl.value === this.dropdownUnsetValue ||
      formControl.value == null ||
      this.normalizeFormValue(formControl.value) === ''
    );
  }

  getDropdownDisplayValue(field: CustomAttributeField): string {
    const formControl = this.userForm.get(field.controlKey + '_FieldValue');
    const value = formControl ? formControl.value : '';
    const normalizedValue = this.normalizeFormValue(value);
    if (normalizedValue) {
      return normalizedValue;
    }
    return (
      this.normalizeFormValue(field.defaultValue) ||
      this.normalizeFormValue(field.entraValue) ||
      ''
    );
  }

  compareDropdownValues = (first: unknown, second: unknown): boolean => {
    return this.normalizeFormValue(first) === this.normalizeFormValue(second);
  };

  isDefaultOrEntraValue(field: CustomAttributeField): boolean {
    const formControl = this.userForm.get(field.controlKey + '_FieldValue');
    const currentValue = this.normalizeFormValue(formControl ? formControl.value : '');
    const defaultValue = this.normalizeFormValue(field.defaultValue);
    const entraValue = this.normalizeFormValue(field.entraValue);
    if (currentValue) {
      return currentValue === defaultValue || currentValue === entraValue;
    }
    return !!defaultValue || !!entraValue;
  }

  isSaveDisabled(): boolean {
    return this.isSaving || this.hasFormValidationError() || !this.hasFormChanges();
  }

  hasFormChanges(): boolean {
    if (!this.userForm) {
      return false;
    }
    return this.formFields
      .filter(field => field.editable)
      .some(field => this.hasFieldChanged(field));
  }

  private hasFieldChanged(field: CustomAttributeField): boolean {
    return this.getEffectiveFieldValue(field) !== this.originalFormValues.get(field.controlKey);
  }

  hasWhitespaceOnlyError(): boolean {
    if (!this.userForm) {
      return false;
    }
    return this.formFields.some(field => {
      if (!field.editable || field.allowedValuesEnabled) {
        return false;
      }
      const formControl = this.userForm.get(field.controlKey + '_FieldValue');
      return !!formControl && formControl.hasError('whitespaceOnly');
    });
  }

  hasMaxLengthValidationError(): boolean {
    if (!this.userForm) {
      return false;
    }
    return this.formFields.some(field => {
      const formControl = this.userForm.get(field.controlKey + '_FieldValue');
      return !!formControl && formControl.hasError('maxlength');
    });
  }

  hasFormValidationError(): boolean {
    return this.hasWhitespaceOnlyError() || this.hasMaxLengthValidationError();
  }

  getFormValidationMessage(): string {
    if (this.hasWhitespaceOnlyError()) {
      return this.whitespaceOnlyErrorMessage;
    }
    if (this.hasMaxLengthValidationError()) {
      return 'Maximum 500 characters allowed.';
    }
    return '';
  }

  private readonly whitespaceOnlyErrorMessage = 'Content cannot contain only spaces.';

  private readonly saveErrorFallback = 'Unable to save. Please try again.';
  private readonly loadErrorFallback = 'Unable to load attributes. Please try again.';

  private resolveApiMessage(value: unknown, fallback: string): string {
    const message = this.extractMessage(value);
    if (message && !this.isUnusableMessage(message)) {
      return message;
    }
    return fallback;
  }

  private resolveSaveErrorMessage(saveError: any): string {
    const fromError = this.extractMessage(saveError?.error?.message)
      || this.extractMessage(saveError?.error)
      || this.extractMessage(saveError?.message)
      || this.extractMessage(saveError);
    if (fromError && !this.isUnusableMessage(fromError)) {
      return fromError;
    }
    return this.saveErrorFallback;
  }

  private extractMessage(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value
        .map(item => this.extractMessage(item))
        .filter(Boolean)
        .join(' ');
    }
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      for (const key of ['message', 'error', 'title', 'detail', 'description']) {
        const extracted = this.extractMessage(obj[key]);
        if (extracted) {
          return extracted;
        }
      }
      if (obj.errors) {
        const extracted = this.extractMessage(obj.errors);
        if (extracted) {
          return extracted;
        }
      }
    }
    return '';
  }

  private isUnusableMessage(message: string): boolean {
    const normalized = message.trim().toLowerCase();
    return !normalized || normalized === '[object object]';
  }

  private getResetValue(field: CustomAttributeField): string {
    return field.entraValue || field.defaultValue || '';
  }

  private readonly whitespaceOnlyValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || value === '') {
      return null;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return { whitespaceOnly: true };
    }
    return null;
  };

  private normalizeFormValue(value: unknown): string {
    if (value == null || value === this.dropdownUnsetValue) {
      return '';
    }
    return String(value).trim();
  }

  private getInitialFieldValue(field: CustomAttributeField): string | null {
    if (field.allowedValuesEnabled) {
      if (this.normalizeFormValue(field.fieldValue)) {
        return field.fieldValue;
      }
      return this.normalizeFormValue(field.entraValue) ? '' : this.dropdownUnsetValue;
    }
    const stored = this.normalizeFormValue(field.fieldValue);
    if (!stored) {
      return '';
    }
    const entra = this.normalizeFormValue(field.entraValue);
    const defaultValue = this.normalizeFormValue(field.defaultValue);
    if (stored === entra || stored === defaultValue) {
      return '';
    }
    return field.fieldValue;
  }

  private applyTextPlaceholderState(field: CustomAttributeField): void {
    if (field.allowedValuesEnabled) {
      return;
    }
    const stored = this.normalizeFormValue(field.fieldValue);
    const entra = this.normalizeFormValue(field.entraValue);
    const defaultValue = this.normalizeFormValue(field.defaultValue);
    if (stored && stored === entra) {
      field.preferEntraPlaceholder = true;
      field.emptyValue = field.entraValue || '';
      return;
    }
    if (stored && stored === defaultValue) {
      field.preferEntraPlaceholder = false;
      field.emptyValue = field.defaultValue || '';
      return;
    }
    field.preferEntraPlaceholder = false;
    field.emptyValue = '';
  }

  private getEffectiveFieldValue(field: CustomAttributeField): string {
    const formControl = this.userForm.get(field.controlKey + '_FieldValue');
    const formValue = this.normalizeFormValue(
      formControl ? formControl.value : field.fieldValue
    );
    if (field.allowedValuesEnabled) {
      return formValue;
    }
    if (formValue) {
      return formValue;
    }
    return this.normalizeFormValue(field.emptyValue);
  }

  private syncTextFieldDisplay(field: CustomAttributeField): void {
    if (field.allowedValuesEnabled) {
      return;
    }
    this.applyTextPlaceholderState(field);
    const formControl = this.userForm.get(field.controlKey + '_FieldValue');
    if (!formControl) {
      return;
    }
    formControl.setValue(this.getInitialFieldValue(field), { emitEvent: false });
  }

  private syncDropdownUnsetFormValue(field: CustomAttributeField): void {
    if (!field.allowedValuesEnabled || !this.showPlaceholderDropdownOption(field)) {
      return;
    }
    const formControl = this.userForm.get(field.controlKey + '_FieldValue');
    if (!formControl) {
      return;
    }
    if (!this.normalizeFormValue(formControl.value)) {
      formControl.setValue(this.dropdownUnsetValue, { emitEvent: false });
    }
  }

  private updateSavedValues(): void {
    this.formFields
      .filter(field => field.editable)
      .forEach(field => {
        const effectiveValue = this.getEffectiveFieldValue(field);
        field.fieldValue = effectiveValue;
        this.originalFormValues.set(field.controlKey, effectiveValue);
        this.syncTextFieldDisplay(field);
        this.syncDropdownUnsetFormValue(field);
      });
    this.userForm.markAsPristine();
  }

  private getValueToSend(value: string, field: CustomAttributeField): string {
    const trimmed = this.normalizeFormValue(value);
    if (!trimmed) {
      return '';
    }
    if (
      field.allowedValuesEnabled &&
      (trimmed === this.normalizeFormValue(field.entraValue) ||
        trimmed === this.normalizeFormValue(field.defaultValue))
    ) {
      return '';
    }
    return trimmed;
  }

  updateFieldSaveMessage(controlKey: string, message: string, messageClass: string): void {
    const field = this.formFields.find(f => f.controlKey === controlKey);
    if (field) {
      field.saveMessage = message;
      field.saveMessageClass = messageClass;
      setTimeout(() => {
        field.saveMessage = '';
      }, 2000);
    }
  }
}
